import React from 'react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { SectionHeading } from '../components/ui/SectionHeading';
import { motion } from 'framer-motion';
import { FileText, DollarSign, Clock, CheckCircle } from 'lucide-react';
const visaOptions = [{
  duration: '15 Days',
  fee: '$30 USD',
  extension: 'Can be extended'
}, {
  duration: '30 Days',
  fee: '$50 USD',
  extension: 'Can be extended'
}, {
  duration: '90 Days',
  fee: '$125 USD',
  extension: 'Maximum stay per year'
}];
const requirements = ['Valid passport (minimum 6 months validity)', 'Passport-sized photo (can be taken at airport)', 'Visa fee in USD cash', 'Completed visa application form', 'Return flight ticket (may be requested)', 'Proof of accommodation (hotel booking)'];
export function NepalVisaPage() {
  return <div className="min-h-screen bg-white font-sans" data-id="element-1572">
      <Navbar data-id="element-1573" />

      {/* Hero Section */}
      <div className="relative h-[60vh] min-h-[500px] flex items-center justify-center" data-id="element-1574">
        <div className="absolute inset-0 bg-cover bg-center z-0" style={{
        backgroundImage: 'url("https://images.unsplash.com/photo-1605640840605-14ac1855827b?q=80&w=1933&auto=format&fit=crop")'
      }} data-id="element-1575">
          <div className="absolute inset-0 bg-gradient-to-b from-green-900/70 via-green-900/50 to-green-900/80" data-id="element-1576" />
        </div>
        <div className="relative z-10 text-center text-white px-4 max-w-4xl mx-auto" data-id="element-1577">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8
        }} className="flex justify-center mb-6" data-id="element-1578">
            <div className="w-16 h-16 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center" data-id="element-1579">
              <FileText className="text-white" size={32} data-id="element-1580" />
            </div>
          </motion.div>
          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 0.2
        }} className="text-5xl md:text-6xl font-bold mb-6 tracking-tight" data-id="element-1581">
            Nepal Visa & Entry Requirements
          </motion.h1>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 0.4
        }} className="text-xl md:text-2xl text-green-100 font-light max-w-2xl mx-auto" data-id="element-1582">
            Everything you need to know about entering Nepal
          </motion.p>
        </div>
      </div>

      {/* Overview */}
      <section className="py-20 px-4 max-w-4xl mx-auto" data-id="element-1583">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1584">
          <SectionHeading title="Tourist Visa for Nepal" subtitle="Overview" data-id="element-1585" />
          <div className="prose prose-lg max-w-none text-stone-700 leading-relaxed space-y-4" data-id="element-1586">
            <p data-id="element-1587">
              Most foreign nationals require a visa to enter Nepal. The good
              news is that tourist visas are available on arrival at Tribhuvan
              International Airport in Kathmandu and at land border crossings.
              The process is straightforward and typically takes 15-30 minutes.
            </p>
            <p data-id="element-1588">
              You can also apply for a visa online before arrival through the
              Nepal Immigration Department's online portal. This pre-application
              can speed up the process at the airport, though you'll still need
              to pay and collect your visa on arrival.
            </p>
          </div>
        </motion.div>
      </section>

      {/* Visa Options */}
      <section className="bg-green-50 py-20 px-4" data-id="element-1589">
        <div className="max-w-7xl mx-auto" data-id="element-1590">
          <SectionHeading title="Visa Options & Fees" subtitle="Choose your duration" data-id="element-1591" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1592">
            {visaOptions.map((option, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1
          }} className="bg-white rounded-2xl p-8 shadow-lg text-center" data-id="element-1593">
                <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-500 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-1594">
                  <Clock className="text-white" size={32} data-id="element-1595" />
                </div>
                <h3 className="text-3xl font-bold text-stone-900 mb-2" data-id="element-1596">
                  {option.duration}
                </h3>
                <p className="text-4xl font-bold text-green-600 mb-4" data-id="element-1597">
                  {option.fee}
                </p>
                <p className="text-stone-600 text-sm" data-id="element-1598">{option.extension}</p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Requirements */}
      <section className="py-20 px-4 max-w-4xl mx-auto" data-id="element-1599">
        <SectionHeading title="Required Documents" subtitle="What you need to bring" data-id="element-1600" />
        <div className="bg-white rounded-2xl shadow-lg p-8" data-id="element-1601">
          <ul className="space-y-4" data-id="element-1602">
            {requirements.map((req, index) => <motion.li key={index} initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1
          }} className="flex items-start gap-4" data-id="element-1603">
                <CheckCircle className="text-green-500 shrink-0 mt-1" size={24} data-id="element-1604" />
                <span className="text-stone-700 text-lg" data-id="element-1605">{req}</span>
              </motion.li>)}
          </ul>
        </div>
      </section>

      {/* Process */}
      <section className="bg-stone-50 py-20 px-4" data-id="element-1606">
        <div className="max-w-4xl mx-auto" data-id="element-1607">
          <SectionHeading title="Visa on Arrival Process" subtitle="Step by step" data-id="element-1608" />
          <div className="space-y-6" data-id="element-1609">
            {[{
            step: '1',
            title: 'Fill Out Form',
            desc: 'Complete the visa application form at the airport kiosk or online beforehand'
          }, {
            step: '2',
            title: 'Submit Documents',
            desc: 'Present your passport, photo, and completed form at the visa counter'
          }, {
            step: '3',
            title: 'Pay Fee',
            desc: 'Pay the visa fee in USD cash (exact amount preferred)'
          }, {
            step: '4',
            title: 'Receive Visa',
            desc: 'Your visa will be stamped in your passport within minutes'
          }, {
            step: '5',
            title: 'Immigration',
            desc: 'Proceed to immigration counter for entry stamp'
          }].map((item, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1
          }} className="bg-white rounded-xl p-6 flex items-start gap-4" data-id="element-1610">
                <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center shrink-0" data-id="element-1611">
                  <span className="text-white font-bold text-lg" data-id="element-1612">
                    {item.step}
                  </span>
                </div>
                <div data-id="element-1613">
                  <h3 className="text-lg font-bold text-stone-900 mb-2" data-id="element-1614">
                    {item.title}
                  </h3>
                  <p className="text-stone-600" data-id="element-1615">{item.desc}</p>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Important Notes */}
      <section className="py-20 px-4 max-w-4xl mx-auto" data-id="element-1616">
        <SectionHeading title="Important Information" subtitle="Things to know" data-id="element-1617" />
        <div className="space-y-4" data-id="element-1618">
          <div className="bg-blue-50 rounded-xl p-6 border-l-4 border-blue-600" data-id="element-1619">
            <h3 className="font-bold text-blue-900 mb-2" data-id="element-1620">Visa Extensions</h3>
            <p className="text-blue-800" data-id="element-1621">
              Tourist visas can be extended at the Department of Immigration in
              Kathmandu or Pokhara. Maximum stay is 150 days per calendar year.
            </p>
          </div>
          <div className="bg-amber-50 rounded-xl p-6 border-l-4 border-amber-600" data-id="element-1622">
            <h3 className="font-bold text-amber-900 mb-2" data-id="element-1623">Trekking Permits</h3>
            <p className="text-amber-800" data-id="element-1624">
              Separate trekking permits (TIMS card and national park permits)
              are required for trekking. We handle all permit arrangements for
              our clients.
            </p>
          </div>
          <div className="bg-green-50 rounded-xl p-6 border-l-4 border-green-600" data-id="element-1625">
            <h3 className="font-bold text-green-900 mb-2" data-id="element-1626">Currency</h3>
            <p className="text-green-800" data-id="element-1627">
              Bring USD cash for visa fees. ATMs are available in Kathmandu for
              Nepali Rupees. Credit cards are accepted at major hotels but cash
              is king in Nepal.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-4 bg-gradient-to-r from-green-600 to-emerald-600 text-white" data-id="element-1628">
        <div className="max-w-4xl mx-auto text-center" data-id="element-1629">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} data-id="element-1630">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-1631">
              Need Help with Visa & Permits?
            </h2>
            <p className="text-xl text-green-100 mb-8" data-id="element-1632">
              We assist with all documentation and permit arrangements
            </p>
            <a href="#contact" data-id="element-1633">
              <button className="bg-white text-green-600 px-8 py-4 rounded-full font-semibold hover:bg-green-50 transition-colors" data-id="element-1634">
                Contact Us
              </button>
            </a>
          </motion.div>
        </div>
      </section>

      <Footer data-id="element-1635" />
    </div>;
}